import React, { forwardRef, useImperativeHandle } from 'react';
import { FormBuilder, FormSchema, FormBuilderRef } from '../../../components/formBuilder';
import { MailReminder, MailReminderStrategy } from '../../../models/mailReminder';
import { enumToOptions } from '../../../utils/misc';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const EMAIL_PATTERN = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const maxLengthRule = (t: TFunction, max: number) => (value?: string) => ((value && value.length > max) ? t('Must be {{max}} characters or fewer', { max }) : '');

const emailRule = (t: TFunction) => (value?: string) => ((value && !EMAIL_PATTERN.test(value)) ? t('Invalid email address') : '');

export type FormRef = {
  submit: () => Promise<void>;
}

type Props = {
  formData: Partial<MailReminder>;
  actionText?: string,
  setFormData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  handleSubmit: () => Promise<void>
  onValidationChange?: (hasErrors: boolean) => void;
}

const Form = forwardRef<FormRef, Props>(({
  formData,
  actionText,
  setFormData,
  handleSubmit,
  onValidationChange
}, ref) => {
  const { t } = useTranslation();

  const [formSchema, setFormSchema] = React.useState<FormSchema[]>([
    {
      name: 'email',
      label: t('Email Reminder Details'),
      icon: '✉️',
      fields: [
        {
          name: 'emailSubject', type: 'text', label: t('Subject'), required: true, disable: formData?.id ? true : false,
          validationRules: [maxLengthRule(t, 400)],
        },
        {
          name: 'emailTo', type: 'multiple', label: t('To'), required: true, cols: 6, disable: formData?.id ? true : false,
          validationRules: [emailRule(t), maxLengthRule(t, 200)],
        },
        {
          name: 'emailCc', type: 'multiple', label: t('Cc'), required: false, cols: 6, disable: formData?.id ? true : false,
          validationRules: [emailRule(t), maxLengthRule(t, 200)],
        },
        {
          name: 'emailBcc', type: 'multiple', label: t('Bcc'), required: false, cols: 6, disable: formData?.id ? true : false,
          validationRules: [emailRule(t), maxLengthRule(t, 200)],
        },
        {
          name: 'emailReplyTo', type: 'email', label: t('Reply to'), required: false, cols: 6, disable: formData?.id ? true : false,
          validationRules: [emailRule(t), maxLengthRule(t, 200)],
        },
        {
          name: 'emailBody', type: 'multiline', label: t('Email body'), required: false, rows: 4, disable: formData?.id ? true : false,
          validationRules: [maxLengthRule(t, 10000)],
        },
        {
          name: 'emailBodyGenerationPrompt', type: 'multiline', label: t('Email body generation prompt'), required: false, rows: 2, disable: formData?.id ? true : false,
          validationRules: [maxLengthRule(t, 2000)],
        },
      ]
    },
    {
      name: 'additionalInfo',
      label: t('Additional Information'),
      icon: '📝',
      fields: [
        { name: 'strategy', type: 'select', label: t('Strategy'), required: true, options: enumToOptions(MailReminderStrategy), cols: 12, disable: formData?.id ? true : false },
        { name: 'firstSentNotBefore', type: 'date', label: t('First sent not before'), required: false, cols: 6 },
        { name: 'lastSentNotAfter', type: 'date', label: t('Last sent not after'), required: false, cols: 6, disable: formData?.id ? true : false },
        { name: 'active', type: 'switch', label: t('Active'), required: false },
        {
          name: 'content', type: 'upload', label: t('Attachment (PDF)'), required: true, validationRules: [
            (value) => ((!value && !formData.contentHandle) ? t('Required!') : ''),
            (value, meta) => {
              if (!value) return '';
              const fileType = meta?.fileType ?? meta?.contentFileType;
              return fileType && fileType !== 'application/pdf' ? t('File format isn’t supported.') : '';
            }
          ]
        },
      ]
    }
  ]);

  const formBuilderRef = React.useRef<FormBuilderRef>(null);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (formBuilderRef.current) {
        await formBuilderRef.current.submit();
      }
    }
  }));

  return (
    <FormBuilder
      ref={formBuilderRef}
      formSchema={formSchema}
      formData={formData}
      setFormData={setFormData}
      handleSubmit={handleSubmit}
      actionText={actionText}
      onSchemaChange={setFormSchema}
      onValidationChange={onValidationChange}
    />
  );
});

Form.displayName = 'Form';

export default Form;
